import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Cloud, Calendar, Brain, Activity, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";
import { BehavioralActivation } from "./exercises/BehavioralActivation";

interface DepressionToolsProps {
  user: any;
}

export const DepressionTools = ({ user }: DepressionToolsProps) => {
  const [activeTab, setActiveTab] = useState('activation');
  const [activeExercise, setActiveExercise] = useState<string | null>(null);

  const tools = [
    {
      id: 'activation',
      name: 'Behavioural Activation',
      icon: Activity,
      description: 'Schedule activities that align with your values and bring pleasure or mastery',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      why: 'Depression often leads to withdrawal and inactivity, which creates a vicious cycle. Behavioural activation breaks this cycle by gradually reintroducing meaningful activities. Research shows that action comes before motivation - you don\'t need to "feel like it" to start.',
      how: 'Start small with achievable activities. Rate each activity for pleasure (P) and mastery (M) out of 10. Notice that doing things, even when you don\'t feel like it, can shift your mood. Build momentum gradually.',
      science: 'Behavioral activation is as effective as antidepressants for mild to moderate depression. It works by disrupting avoidance patterns and increasing positive reinforcement from your environment, which helps regulate mood-related neurotransmitters.'
    },
    {
      id: 'scheduling',
      name: 'Activity Scheduling',
      icon: Calendar,
      description: 'Plan your day to include balanced activities across different categories',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      why: 'When depressed, unstructured time can feel overwhelming and lead to rumination. Having a schedule provides structure, reduces decision fatigue, and ensures you\'re including activities that support recovery.',
      how: 'Create a weekly schedule that includes: 1) Necessary activities (hygiene, meals), 2) Pleasant activities (even small ones), 3) Achievement activities (tasks that give a sense of accomplishment), 4) Social connection, 5) Physical movement. Start with just 2-3 activities per day.',
      science: 'Activity scheduling helps counter the reduced reward sensitivity seen in depression. By systematically exposing yourself to potentially rewarding experiences, you help retrain your brain\'s reward circuits.'
    },
    {
      id: 'thought-records',
      name: 'Thought Records',
      icon: Brain,
      description: 'Identify and challenge negative automatic thoughts that maintain depression',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      why: 'Depression involves thinking patterns characterized by negative views of yourself, the world, and the future (Beck\'s cognitive triad). These thoughts feel true but are often distorted. Examining them helps you see alternatives.',
      how: 'When you notice a mood shift, write down: 1) The situation, 2) Your automatic thought, 3) The emotion and intensity (0-100), 4) Evidence for and against the thought, 5) A more balanced thought, 6) New emotion rating. Do this regularly, not just when in crisis.',
      science: 'Thought records are a core component of CBT for depression. They help identify cognitive distortions (like all-or-nothing thinking, overgeneralization, mental filtering) and develop more flexible, balanced thinking patterns that reduce depressive symptoms.'
    },
    {
      id: 'pleasure-mastery',
      name: 'Pleasure & Mastery Log',
      icon: CheckCircle2,
      description: 'Track activities and rate them for enjoyment and sense of accomplishment',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      why: 'Depression makes it hard to experience pleasure (anhedonia) and feel a sense of accomplishment. Tracking helps you notice that some activities, even if not enjoyable beforehand, can bring some pleasure or mastery during or after.',
      how: 'For each activity throughout your day, rate: P (Pleasure 0-10) - how much you enjoyed it, and M (Mastery 0-10) - sense of achievement. Do this for several days to identify patterns. Notice that ratings during activities are often higher than predicted beforehand.',
      science: 'This technique helps identify activities that provide the most reward value for your effort. It counteracts the "nothing helps" belief common in depression and provides objective data about what actually impacts your mood.'
    }
  ];

  // If an exercise is active, render it
  if (activeExercise === 'activation') {
    return <BehavioralActivation userId={user.id} onBack={() => setActiveExercise(null)} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-br from-background to-primary/5 border-primary/20">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-lg bg-primary/10">
              <Cloud className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">Depression Support Tools</CardTitle>
              <CardDescription>Evidence-based interventions for low mood and behavioral activation</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="bg-background/50 backdrop-blur-sm rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium flex items-center gap-2">
              <Info className="h-4 w-4 text-primary" />
              Understanding Depression
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Depression isn't weakness or laziness-it's a medical condition affecting mood, thinking, and behaviour. 
              The tools here focus on behavioral activation (taking action before motivation arrives) and cognitive 
              restructuring (examining and challenging depressive thoughts). Small, consistent steps are more effective 
              than trying to do everything at once.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tools Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex overflow-x-auto scrollbar-hide w-full gap-1 bg-gradient-calm border border-border/50 p-1 justify-start sm:justify-center">
          {tools.map((tool) => {
            const Icon = tool.icon;
            return (
              <TabsTrigger 
                key={tool.id} 
                value={tool.id}
                className="gap-1.5 shrink-0 px-3 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">{tool.name.split(' ')[0]}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {tools.map((tool) => {
          const Icon = tool.icon;
          return (
            <TabsContent key={tool.id} value={tool.id} className="mt-6 space-y-6">
              <Card className="border-border/50">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${tool.bgColor}`}>
                      <Icon className={`h-5 w-5 ${tool.color}`} />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">{tool.name}</CardTitle>
                      <CardDescription>{tool.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Why This Matters */}
                  <div className="bg-gradient-to-br from-primary/15 to-secondary/10 rounded-lg p-4 space-y-2">
                    <p className="text-base font-medium text-foreground font-aileron">💡 Why This Matters</p>
                    <p className="text-sm text-foreground leading-relaxed">
                      {tool.why}
                    </p>
                  </div>

                  {/* How to Use It */}
                  <div className="bg-gradient-to-br from-secondary/15 to-primary/10 rounded-lg p-4 space-y-2">
                    <p className="text-base font-medium text-foreground font-aileron">🔧 How to Use This Tool</p>
                    <p className="text-sm text-foreground leading-relaxed">
                      {tool.how}
                    </p>
                  </div>

                  {/* The Science */}
                  <div className="bg-gradient-to-br from-primary/15 to-secondary/10 rounded-lg p-4 space-y-2">
                    <p className="text-base font-medium text-foreground font-aileron">🧠 The Science</p>
                    <p className="text-sm text-foreground leading-relaxed">
                      {tool.science}
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button 
                      className="flex-1 text-base font-aileron" 
                      onClick={() => {
                        if (tool.id === 'activation' || tool.id === 'pleasure-mastery') {
                          setActiveExercise('activation');
                        } else {
                          toast.info(`${tool.name} exercise coming soon!`);
                        }
                      }}
                    >
                      Start Exercise
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1 text-base font-aileron" 
                      onClick={() => toast.info(`${tool.name} examples coming soon!`)}
                    >
                      View Examples
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Key Principles */}
      <Card className="bg-gradient-to-br from-primary/15 to-secondary/10 border-primary/30">
        <CardContent className="pt-6">
          <div className="space-y-3">
            <p className="text-base font-medium font-aileron text-foreground">Key Principles for Working with Depression:</p>
            <div className="space-y-2 text-sm text-foreground">
              <p><strong>1. Action before motivation:</strong> You don't need to feel motivated to take action. Action often creates motivation, not the other way around.</p>
              <p><strong>2. Small steps count:</strong> Taking a shower, making your bed, or going for a 5-minute walk are genuine achievements when depressed.</p>
              <p><strong>3. Be specific:</strong> "Exercise more" is vague. "Walk around the block at 10am on Monday" is actionable.</p>
              <p><strong>4. Notice the nuance:</strong> Depression makes everything feel "all bad" or "pointless." Reality is more nuanced-look for evidence of small positives.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};